/**
 * Staal-formaatkiezer — de sheet "Welke staal wil je?" (SHO-9835).
 *
 * 23 behangstalen hebben sinds INV-433 drie formaten als varianten onder de optie `Formaat`:
 * Standaard, M en XL. Elke gratis-staal-knop in het thema pakte tot nu toe blind de eerste
 * variant, waardoor M en XL nergens te koop waren behalve op de staal-PDP zelf.
 *
 * Deze module vangt de klik op elke knop die een `data-staal-varianten`-array meedraagt en
 * opent in plaats daarvan de sheet. Knoppen zonder dat attribuut — de ~8.900 stalen met één
 * formaat — raakt hij niet aan; die blijven één tik.
 *
 * VIER INGANGEN, ÉÉN ONDERSCHEPPING. De knoppen komen uit vier plekken met elk hun eigen
 * klik-afhandeling (PDP-knop via hds-pdp-delegation.js, collectiekaart via staalkiezer.js,
 * sticky balk, verlanglijst). In plaats van ze alle vier te leren wat formaten zijn, luistert
 * deze module in de CAPTURE-fase en stopt hij het event daar. De twee bestaande handlers
 * hebben een guard gekregen die op hetzelfde attribuut kijkt, zodat ze ook niets doen als de
 * volgorde van registreren ooit verandert.
 *
 * MORPH-BESTENDIG. Bij een combined-listing-switch morpht het thema de hele <main> en zijn
 * closures over knoppen waardeloos. Daarom: delegatie vanuit dit asset, dat op elke relevante
 * pagina één keer geladen wordt, en de
 * formaten worden op KLIK-MOMENT uit het dataset van de knop gelezen — nooit uit een closure.
 * Zelfde regel als in hds-pdp-delegation.js.
 */

import { getCart } from '@theme/cart-state';

/**
 * Stalen tot dit bedrag tellen mee voor de gratis-regel. 0 = geen drempel, elk staal telt.
 *
 * De waarde komt uit layout/theme.liquid (500) en niet uit deze constante — de constante is
 * alleen de val-terug als het attribuut ontbreekt.
 *
 * Achtergrond: gemeten op de live winkel (25-08-2026) gaf de korting "3 gratis per categorie"
 * de eerste drie stalen in mand-volgorde weg, ongeacht formaat. Standaard €2 + M €15 + XL €35
 * kostten samen €0. Dat is een fout in de korting-function, verholpen in SHO-9836: sindsdien
 * komt alleen het Standaard-formaat (en elk staal zonder formaatkeuze) in aanmerking.
 */
const GRATIS_DREMPEL_STANDAARD = 0;

/** Hoogte van het tekenvlak. Gelijk aan --staal-sheet-vlak-hoogte in de CSS. */
const VLAK_HOOGTE_PX = 170;

const SELECTOR_KNOP = '[data-staal-varianten]';

/* --------------------------------------------------------------------- helpers */

function bedrag(centen) {
  const valuta = window.Shopify?.currency?.active || 'EUR';
  try {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: valuta }).format(centen / 100);
  } catch {
    return `€ ${(centen / 100).toFixed(2).replace('.', ',')}`;
  }
}

/**
 * Meet-events naar de Shopify Web Pixels, met het hds:-prefix van de andere theme-events.
 * Faalt stil: meten mag winkelen nooit breken. Zelfde opzet als staalkiezer.js.
 */
function publiceerMeetEvent(naam, payload) {
  try {
    window.Shopify?.analytics?.publish?.(naam, payload);
  } catch {
    /* geen analytics beschikbaar */
  }
}

async function leesWinkelmand({ vers = false } = {}) {
  try {
    return await getCart({ vers });
  } catch {
    return null;
  }
}

/** Een winkelmandregel is een staal als het producttype dat zegt. */
/**
 * Is deze winkelmandregel een staal? Beide woorden testen, want het producttype is
 * "Behang stalen" en 'stalen' bevat 'staal' niet (één a). Zelfde test als in
 * snippets/staal-knop.liquid.
 */
function isStaalRegel(item) {
  const soort = (item?.product_type || '').toLowerCase();
  return soort.includes('stalen') || soort.includes('staal');
}

/**
 * Laat de rest van de pagina weten dat de winkelmand wijzigde: de header-teller, de drawer
 * en staalkiezer.js hangen daaraan. Bewust ZONDER de drawer te openen — in deze sheet pakt
 * de klant er vaak twee, en een drawer die over de sheet heen klapt breekt dat af. Het vinkje
 * op de knop bevestigt het hier, en onderin verschijnt de weg naar de mand.
 */
async function meldCartMutatie(cart) {
  try {
    const { CartUpdateEvent } = await import('@theme/events');
    if (CartUpdateEvent) {
      document.dispatchEvent(
        new CartUpdateEvent({}, 'staal-formaatkiezer', {
          source: 'staal-formaatkiezer',
          itemCount: cart?.item_count,
        })
      );
      return;
    }
  } catch {
    /* val terug op een gewoon event */
  }

  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: { sourceId: 'staal-formaatkiezer', data: { source: 'staal-formaatkiezer' } },
    })
  );
}

/* ------------------------------------------------------------------- element */

class StaalFormaatkiezer extends HTMLElement {
  #dialog;
  #knop = null;
  #opties = [];
  #cart = null;
  #cartGelezen = false;
  #beeld = '';

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.gratisAantal = Number(this.dataset.gratisAantal || 3);
    this.gratisDrempel = Number(this.dataset.gratisDrempel || GRATIS_DREMPEL_STANDAARD);

    this.removeAttribute('hidden');

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-staal-sheet-sluit]')) this.sluit();
    });

    // Klik naast het paneel sluit de sheet. De <dialog> zelf vult het scherm, het paneel
    // niet: een klik die de dialog als target heeft, viel dus buiten het paneel.
    this.#dialog?.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.sluit();
    });

    this.#dialog?.addEventListener('close', () => {
      this.#knop = null;
    });
  }

  /**
   * @param {HTMLElement} knop De knop waarop geklikt is; blijft de bron van waarheid voor
   *   titel, beeld en de formaten, en krijgt na afloop de juiste staat terug.
   */
  open(knop) {
    let opties;
    try {
      opties = JSON.parse(knop.dataset.staalVarianten || '[]');
    } catch {
      return false;
    }
    if (!Array.isArray(opties) || opties.length < 2) return false;

    this.#knop = knop;
    this.#opties = opties;
    this.#cart = null;
    this.#cartGelezen = false;
    this.#beeld = this.#beeldVanPagina(knop) || knop.dataset.staalBeeld || '';

    const titel = knop.dataset.staalTitel || '';
    // "Staal Arte Manila …" eraf: in de sheet gaat het over het behang, niet over het
    // artikelnummer waaronder het staal in de catalogus staat. De staart "op ware
    // verhouding" hoort bij de tekening eronder en blijft staan als de titel wegvalt.
    const behang = titel.replace(/^staal\s+/i, '').trim();
    const metaRegel = this.querySelector('[data-staal-sheet-product]');
    if (metaRegel) metaRegel.textContent = behang ? `${behang} · op ware verhouding` : 'Op ware verhouding';

    this.#verbergMelding();
    this.#render();

    if (!this.#dialog?.open) this.#dialog?.showModal();

    // Pas ná showModal heeft het paneel een breedte, en die bepaalt de schaal.
    this.#tekenVlakken();

    publiceerMeetEvent('hds:staal_formaatkiezer_open', {
      hoofdproduct_titel: titel,
      hoofdproduct_url: knop.dataset.staalUrl || '',
      aantal_formaten: opties.length,
      bron: knop.dataset.staalBron || 'onbekend',
    });

    // De winkelmand bepaalt welke formaten al gekozen zijn en wat het gratis-tegoed is.
    // Asynchroon: de sheet staat al in beeld, de vinkjes en de mand-link volgen.
    this.#ververs();
    return true;
  }

  /**
   * Het beeld dat deze pagina al heeft staan. Een <img> die de bezoeker ziet is gedownload
   * én gedecodeerd, dus die verschijnt in hetzelfde beeldje als de sheet zelf. De URL uit
   * data-staal-beeld is een ándere breedte en daarmee een verse download: op een telefoon
   * zie je de vlakken dan eerst leeg en een tel later pas invullen.
   *
   * Alleen currentSrc telt — dat is precies het bestand dat de browser uit de srcset heeft
   * gekozen en dus in de cache heeft. Is er niets (nog niet geladen, of een knop buiten een
   * kaart), dan blijft data-staal-beeld over.
   */
  #beeldVanPagina(knop) {
    const kaart = knop.closest('.product-card');
    const bron = kaart ? kaart.querySelector('img') : document.querySelector('.product-media img');
    return bron?.currentSrc || '';
  }

  sluit() {
    if (this.#dialog?.open) this.#dialog.close();
  }

  async #ververs() {
    this.#cart = null;
    this.#cartGelezen = false;
    this.#render();
    this.#tekenVlakken();

    this.#cart = await leesWinkelmand();
    this.#cartGelezen = this.#cart !== null;
    if (!this.#cartGelezen) {
      this.#toonMelding('De winkelmand kon niet worden geladen. Sluit dit venster en probeer het opnieuw.');
    } else {
      this.#verbergMelding();
    }
    this.#render();
    this.#tekenVlakken();
    if (this.#cartGelezen) this.#werkBronknopBij();
  }

  /* ------------------------------------------------------------- winkelmand */

  /** Variant-ids van alles wat nu in de winkelmand zit. */
  #idsInMand() {
    return new Set((this.#cart?.items ?? []).map((item) => String(item.variant_id)));
  }

  /** Hoeveel stalen bezetten al een gratis plek, en hoeveel tegoed is er over. */
  #gratisStand() {
    const regels = (this.#cart?.items ?? []).filter(isStaalRegel);
    let gebruikt = 0;
    for (const regel of regels) {
      if (this.gratisDrempel && regel.price > this.gratisDrempel) continue;
      gebruikt += regel.quantity;
    }
    return { gebruikt, over: Math.max(0, this.gratisAantal - gebruikt) };
  }

  /**
   * Kost dit formaat de klant niets?
   *
   * Zit het formaat al in de mand, dan raden we niets: de winkelmand heeft de automatische
   * korting dan al toegepast en final_line_price is de waarheid. Zit het er nog niet in, dan
   * is er tegoed over ⇒ gratis (klopt exact zolang de mand onder het aantal gratis plekken
   * blijft). Is het tegoed op, dan tonen we de eigen prijs; de kassa herverdeelt dan naar de
   * goedkoopste drie, dus wat hier staat kan bij de kassa nog gunstiger uitvallen; de mand
   * zelf blijft de waarheid.
   */
  #isGratis(optie, inMand) {
    if (inMand) {
      const regel = (this.#cart?.items ?? []).find((item) => String(item.variant_id) === String(optie.id));
      if (regel) return (regel.final_line_price ?? regel.line_price ?? 0) === 0;
    }
    if (this.gratisDrempel && optie.prijs > this.gratisDrempel) return false;
    return this.#gratisStand().over > 0;
  }

  /* ----------------------------------------------------------------- render */

  #render() {
    const ids = this.#idsInMand();

    const vlakken = this.querySelector('[data-staal-sheet-vlakken]');
    const opties = this.querySelector('[data-staal-sheet-opties]');
    if (!vlakken || !opties) return;

    vlakken.replaceChildren();
    opties.replaceChildren();

    for (const optie of this.#opties) {
      const gekozen = ids.has(String(optie.id));
      vlakken.append(this.#vlakKolom(optie));
      opties.append(this.#optieKolom(optie, gekozen));
    }

    this.#renderVoet();
  }

  #vlakKolom(optie) {
    const knop = document.createElement('button');
    knop.type = 'button';
    knop.className = 'staal-sheet__vlak-kolom';
    knop.dataset.staalSheetKies = String(optie.id);
    knop.setAttribute('aria-label', `${optie.naam} kiezen`);
    knop.disabled = !this.#cartGelezen;

    const vlak = document.createElement('span');
    vlak.className = 'staal-sheet__vlak';
    vlak.dataset.staalSheetVlak = String(optie.id);
    if (!optie.breedte || !optie.hoogte) vlak.classList.add('staal-sheet__vlak--onbekend');
    if (this.#beeld) vlak.style.backgroundImage = `url("${this.#beeld}")`;

    knop.append(vlak);
    return knop;
  }

  #optieKolom(optie, gekozen) {
    const kolom = document.createElement('span');
    kolom.className = 'staal-sheet__optie';

    const naam = document.createElement('span');
    naam.className = 'staal-sheet__optie-naam';
    naam.textContent = optie.naam;
    kolom.append(naam);

    const maat = document.createElement('span');
    maat.className = 'staal-sheet__optie-maat';
    // Geen maten bekend: dan liever niets dan een verzonnen getal.
    maat.textContent = optie.breedte && optie.hoogte ? `${optie.breedte} × ${optie.hoogte} cm` : ' ';
    kolom.append(maat);

    const gratis = this.#cartGelezen && this.#isGratis(optie, gekozen);
    const prijs = document.createElement('span');
    prijs.className = `staal-sheet__optie-prijs${gratis ? ' staal-sheet__optie-prijs--gratis' : ''}`;
    prijs.textContent = this.#cartGelezen ? (gratis ? 'Gratis' : bedrag(optie.prijs)) : '…';
    kolom.append(prijs);

    // Prijs staat bóven de knop en nooit erin: in de knop leest een bedrag als de prijs van
    // het klikken zelf, en dat maakt "Toevoegen" een ander gebaar dan op de collectiepagina.
    kolom.append(this.#optieKnop(optie, gekozen));
    return kolom;
  }

  /**
   * Exact dezelfde knop als op de collectiekaart: class `staal-knop`, hetzelfde SVG-icoon en
   * dezelfde laadstaat uit staal-knop.css. Geen nagebouwde look-alike — dan lopen rondingen,
   * hover-tokens en het plus-naar-kruis-gebaar op termijn uit elkaar. `staal-sheet__knop` doet
   * alleen de plaatsing binnen de kolom.
   */
  #optieKnop(optie, gekozen) {
    const knop = document.createElement('button');
    knop.type = 'button';
    knop.className = `staal-knop staal-sheet__knop${gekozen ? ' is-gekozen' : ''}`;
    knop.dataset.staalSheetKies = String(optie.id);
    knop.setAttribute('aria-pressed', gekozen ? 'true' : 'false');
    knop.disabled = !this.#cartGelezen;

    knop.insertAdjacentHTML(
      'afterbegin',
      `<svg class="staal-knop__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path class="staal-knop__icon-plus" d="M8 3.5v9M3.5 8h9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
        <path class="staal-knop__icon-vink" d="M3.5 8.5l3 3 6-6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`,
    );

    const label = document.createElement('span');
    label.dataset.staalKnopLabel = '';
    label.textContent = gekozen ? 'Toegevoegd' : 'Toevoegen';
    knop.append(label);

    const laden = document.createElement('span');
    laden.className = 'staal-knop__laden';
    laden.innerHTML = '<span class="staal-knop__spinner"></span>';
    knop.append(laden);

    return knop;
  }

  /**
   * Schaalgetrouw tekenen: één schaalfactor voor alle formaten, zodat het hoogteverschil
   * tussen Standaard, M en XL klopt. De factor is de kleinste van twee grenzen — de hoogte
   * van het tekenvlak en de breedte van een kolom — anders zou een brede baan (sommige
   * collecties zijn 90 cm) buiten zijn kolom lopen.
   */
  #tekenVlakken() {
    const metMaten = this.#opties.filter((optie) => optie.breedte > 0 && optie.hoogte > 0);
    if (!metMaten.length) return;

    const rij = this.querySelector('[data-staal-sheet-vlakken]');
    if (!rij) return;

    const kolommen = this.#opties.length || 1;
    const gap = 8;
    const kolomBreedte = Math.max(24, (rij.clientWidth - gap * (kolommen - 1)) / kolommen);

    const maxHoogte = Math.max(...metMaten.map((optie) => optie.hoogte));
    const maxBreedte = Math.max(...metMaten.map((optie) => optie.breedte));
    const schaal = Math.min(VLAK_HOOGTE_PX / maxHoogte, kolomBreedte / maxBreedte);

    // Het beeld toont een stuk behang ter breedte van de baan, en dat is de breedte van het
    // grootste formaat hier (M en XL worden over de volle rolbreedte gesneden). Door het in
    // elk vlak op diezelfde breedte te zetten staat het patroon overal op één schaal — het
    // hele punt van deze tekening. Met de CSS-standaard `cover` rekt elk vlak het beeld tot
    // zijn eigen verhouding op: de XL is smal en hoog, dus daar zoomde het patroon ver in en
    // stond het naast de M ineens twee keer zo groot.
    const beeldBreedte = Math.round(maxBreedte * schaal);

    for (const optie of metMaten) {
      const vlak = rij.querySelector(`[data-staal-sheet-vlak="${optie.id}"]`);
      if (!(vlak instanceof HTMLElement)) continue;
      vlak.style.width = `${Math.round(optie.breedte * schaal)}px`;
      vlak.style.height = `${Math.round(optie.hoogte * schaal)}px`;
      vlak.style.backgroundSize = `${beeldBreedte}px auto`;
    }
  }

  /** De link naar de mand hoort er pas te staan als er ook echt een staal in ligt. */
  #renderVoet() {
    const balk = this.querySelector('[data-staal-sheet-voet]');
    if (!balk) return;

    const aantal = (this.#cart?.items ?? []).filter(isStaalRegel).length;
    balk.hidden = aantal === 0;
  }

  #toonMelding(bericht) {
    const melding = this.querySelector('[data-staal-sheet-melding]');
    if (!melding) return;
    melding.textContent = bericht;
    melding.hidden = false;
  }

  #verbergMelding() {
    const melding = this.querySelector('[data-staal-sheet-melding]');
    if (!melding) return;
    melding.hidden = true;
    melding.textContent = '';
  }

  /* -------------------------------------------------------------- mutaties */

  async kies(variantId, knop) {
    if (!this.#cartGelezen || knop.dataset.bezig === 'true') return;

    const optie = this.#opties.find((item) => String(item.id) === String(variantId));
    if (!optie) return;

    const zatErAl = this.#idsInMand().has(String(variantId));
    knop.dataset.bezig = 'true';
    knop.setAttribute('aria-busy', 'true');
    this.#verbergMelding();

    try {
      this.#cart = zatErAl ? await this.#haalWeg(variantId) : await this.#voegToe(variantId);
      this.#meldAnalytics(zatErAl ? 'hds:staal_remove' : 'hds:staal_add', optie);
      this.#render();
      this.#tekenVlakken();
      this.#werkBronknopBij();
      await meldCartMutatie(this.#cart);
    } catch (fout) {
      console.error('[staal-formaatkiezer] winkelmand bijwerken mislukt', fout);
      this.#toonMelding('Dit lukte even niet. Probeer het nog een keer.');
      // De sheet mag niet blijven liegen over wat er in de mand zit.
      await this.#ververs();
    } finally {
      delete knop.dataset.bezig;
      knop.removeAttribute('aria-busy');
    }
  }

  async #voegToe(variantId) {
    const root = window.Shopify?.routes?.root ?? '/';
    const antwoord = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        // _bron reist als line-item property mee tot in de order (underscore = niet zichtbaar
        // voor de klant), zodat staal-orders uit deze flow herkenbaar zijn zonder analytics.
        items: [{ id: Number(variantId), quantity: 1, properties: { _bron: 'staal-formaatkiezer' } }],
      }),
    });
    if (!antwoord.ok) throw new Error(`Winkelmand gaf ${antwoord.status}`);
    const cart = await leesWinkelmand({ vers: true });
    if (!cart) throw new Error('Winkelmand na toevoegen kon niet worden gelezen');
    return cart;
  }

  async #haalWeg(variantId) {
    const root = window.Shopify?.routes?.root ?? '/';
    const antwoord = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: String(variantId), quantity: 0 }),
    });
    // 400 betekent hier meestal dat de regel er al niet meer is. Geen fout voor de klant:
    // we lezen de winkelmand opnieuw en trekken de sheet gelijk met de werkelijkheid.
    if (!antwoord.ok) {
      const cart = await leesWinkelmand({ vers: true });
      if (!cart) throw new Error('Winkelmand na verwijderen kon niet worden gelezen');
      return cart;
    }
    return antwoord.json();
  }

  #meldAnalytics(naam, optie) {
    publiceerMeetEvent(naam, {
      staal_variant_id: String(optie.id),
      staal_formaat: optie.naam,
      hoofdproduct_titel: this.#knop?.dataset.staalTitel || '',
      hoofdproduct_url: this.#knop?.dataset.staalUrl || '',
      staal_prijs_centen: Number(optie.prijs || 0),
      bron: this.#knop?.dataset.staalBron || 'staal-formaatkiezer',
    });
  }

  /**
   * De knop waarop geklikt is moet vertellen wat er gebeurd is. Collectiekaarten krijgen dat
   * van staalkiezer.js zodra het cart-event landt (die kent alle knoppen op de pagina); hier
   * dekken we de knoppen die geen synchronisatie hebben — de PDP-knop en de sticky balk.
   */
  #werkBronknopBij() {
    const knop = this.#knop;
    if (!(knop instanceof HTMLElement)) return;
    if (knop.matches('[data-staal-knop]')) return;

    const gekozenNamen = this.#opties
      .filter((optie) => this.#idsInMand().has(String(optie.id)))
      .map((optie) => optie.naam);

    knop.dataset.staalGekozen = gekozenNamen.join(', ');
    knop.classList.toggle('is-gekozen', gekozenNamen.length > 0);
    knop.setAttribute('aria-pressed', gekozenNamen.length > 0 ? 'true' : 'false');

    const label = knop.querySelector('[data-staal-knop-label]');
    if (label instanceof HTMLElement) {
      const basis = gekozenNamen.length ? knop.dataset.labelGekozen || 'Toegevoegd' : knop.dataset.labelToevoegen;
      label.textContent = gekozenNamen.length ? `${basis} · ${gekozenNamen.join(', ')}` : basis;
    }
  }
}

if (!customElements.get('staal-formaatkiezer')) {
  customElements.define('staal-formaatkiezer', StaalFormaatkiezer);
}

/* ------------------------------------------------------------- onderschepping */

/**
 * Capture-fase, zodat we vóór hds-pdp-delegation.js (ook capture) en staalkiezer.js (bubble)
 * zitten en het event kunnen stoppen. Staat de sheet niet op de pagina, dan doen we niets en
 * houdt de knop zijn oude gedrag — beter een staal in de Standaard-maat dan een dode knop.
 */
document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Klik binnen de sheet: een formaat kiezen.
    const kies = target.closest('[data-staal-sheet-kies]');
    if (kies instanceof HTMLElement) {
      const sheet = kies.closest('staal-formaatkiezer');
      if (sheet instanceof StaalFormaatkiezer) {
        event.preventDefault();
        event.stopPropagation();
        sheet.kies(kies.dataset.staalSheetKies, kies);
        return;
      }
    }

    const knop = target.closest(SELECTOR_KNOP);
    if (!(knop instanceof HTMLElement)) return;

    const sheet = document.querySelector('staal-formaatkiezer');
    if (!(sheet instanceof StaalFormaatkiezer)) return;

    event.preventDefault();
    event.stopPropagation();
    sheet.open(knop);
  },
  { capture: true }
);
